/**
 * PlayerIconEditor 型定義
 *
 * 保護者ページのプレイヤーアイコン変更専用
 * CreateUserWizardと同じUI・機能
 */
import { BaseColorKey } from "features/user/constants";

/**
 * エディタのステップ
 */
export enum EditorStep {
  ICON_METHOD = "icon_method",
  PRESET_ICON = "preset_icon",
  UPLOAD_ICON = "upload_icon",
  PROFILE_AI = "profile_ai",
}

/**
 * アイコン選択方法
 */
export enum IconMethod {
  PRESET = "preset",
  UPLOAD = "upload",
  PROFILE_AI = "profile_ai",
}

/**
 * 性別（3択）
 */
export enum ChildGender {
  MALE = "male",
  FEMALE = "female",
  NONE = "none",
}

/**
 * プロフィール入力のサブステップ
 */
export enum ProfileSubStep {
  GENDER = "gender",
  BIRTHDAY = "birthday",
  FAVORITES = "favorites",
  ICON_PREVIEW = "icon_preview",
}

/**
 * 好きなもの選択のサブステップ
 */
export enum FavoritesSubStep {
  APPEARANCE = "appearance",
  COLOR = "color",
  BEST_SUBJECT = "bestSubject",
}

/**
 * 容姿の型定義
 */
export type AccessoryType = "none" | "megane" | "boushi" | "hairpin" | "ribbon" | "bandaid";
export type HairstyleType = "very_short" | "short" | "medium" | "slightly_long" | "long";
export type BodyTypeType = "thin" | "slightly_thin" | "medium" | "slightly_chubby" | "chubby";
export type FaceShapeType = "round" | "oval" | "oblong" | "square" | "triangle";

/**
 * プロフィール状態（ProfileAIStep用）
 */
export interface EditorProfileState {
  // プロフィール情報
  gender: ChildGender | null;
  birthday: string;
  birthdayYear: number | null;
  birthdayMonth: number | null;
  birthdayDay: number | null;

  // 好きないろ（2段階選択）
  selectedBaseColors: BaseColorKey[];
  favoriteColors: string[];

  // 得意な教科（複数選択）
  bestSubjects: string[];

  // 容姿
  accessory: AccessoryType[];
  hairstyle: HairstyleType | null;
  bodyType: BodyTypeType | null;
  faceShape: FaceShapeType | null;
}

/**
 * 全サブステップ定義（フラット・6ステップ）
 */
export interface AllProfileStepDefinition {
  id: string;
  profileSubStep: ProfileSubStep;
  favoritesSubStep?: FavoritesSubStep;
  labelKey: string;
  number: number;
}

/**
 * 全サブステップ一覧（6ステップ）
 * 順序: 性別 → 誕生日 → みため → 好きないろ → 得意な教科 → アイコン
 */
export const ALL_PROFILE_STEPS: AllProfileStepDefinition[] = [
  {
    id: "gender",
    profileSubStep: ProfileSubStep.GENDER,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_GENDER",
    number: 1,
  },
  {
    id: "birthday",
    profileSubStep: ProfileSubStep.BIRTHDAY,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_BIRTHDAY",
    number: 2,
  },
  {
    id: "appearance",
    profileSubStep: ProfileSubStep.FAVORITES,
    favoritesSubStep: FavoritesSubStep.APPEARANCE,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_APPEARANCE",
    number: 3,
  },
  {
    id: "color",
    profileSubStep: ProfileSubStep.FAVORITES,
    favoritesSubStep: FavoritesSubStep.COLOR,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_COLOR",
    number: 4,
  },
  {
    id: "bestSubject",
    profileSubStep: ProfileSubStep.FAVORITES,
    favoritesSubStep: FavoritesSubStep.BEST_SUBJECT,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_BEST_SUBJECT",
    number: 5,
  },
  {
    id: "icon",
    profileSubStep: ProfileSubStep.ICON_PREVIEW,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_ICON",
    number: 6,
  },
];

/**
 * エディタの状態
 */
export interface EditorState {
  // アイコン情報
  icon: string;
  iconMethod: IconMethod | null;
}

/**
 * プロフィールデータ（保存用）
 */
export interface ProfileData {
  icon: string;
  gender?: string | null;
  birthday?: string;
  favorite_color?: string[];
  favorite_animal?: string[];
  favorite_food?: string[];
  strength?: string[];
  interests?: string[];
  self_introduction?: string;
  /** 得意な教科 */
  best_subject?: string[];
  /** 身につけるもの */
  accessory?: string[];
  /** 髪型 */
  hairstyle?: string | null;
  /** 体型 */
  body_type?: string | null;
  /** 顔の形 */
  face_shape?: string | null;
}

/**
 * PlayerIconEditor のプロパティ
 */
export interface PlayerIconEditorProps {
  open: boolean;
  onClose: () => void;
  targetUid: string;
  currentIcon?: string;
  // プロフィール情報（AI生成用）
  currentGender?: string;
  currentBirthday?: string;
  currentFavoriteColor?: string[];
  currentBestSubject?: string[];
  currentSelfIntroduction?: string;
  currentAccessory?: AccessoryType[];
  currentHairstyle?: string;
  currentBodyType?: string;
  currentFaceShape?: string;
  onSelect: (data: ProfileData) => void;
}

/**
 * ステップコンポーネント共通プロパティ
 */
export interface StepProps {
  state: EditorState;
  updateState: (updates: Partial<EditorState>) => void;
}

/**
 * 初期状態
 */
export const initialState: EditorState = {
  icon: "",
  iconMethod: null,
};
